import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./styles/globals.css";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ui/toast";
import { startMockServiceWorker } from "./mocks/browser";
import { MutationErrorReporter, defaultMutationOnError } from "./lib/query/mutationErrorReporter";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // heshe reads-only / short-lived cache by default; mutations decide
      // per-key staleness. Avoid premature network IO during dev.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    // W39.65 — a mutation whose failure nobody reports is a silent one. The
    // default does nothing on its own; its presence is the marker
    // `<MutationErrorReporter />` reads to tell "the call site said nothing"
    // from "the call site handled it". Mounted below, inside ToastProvider,
    // because the toast is React context.
    mutations: { onError: defaultMutationOnError },
  },
});

// In W1 we have no real backend on every dev box; start MSW so primitives /
// patterns / AppShell can render data shapes before backend is wired.
async function bootstrap() {
  if (import.meta.env.DEV) {
    try {
      await startMockServiceWorker();
    } catch (err) {
      // MSW is opt-in; missing mockServiceWorker.js shouldn't block dev.
      console.warn("[heshe] MSW not started (likely no mocks registered):", err);
    }
  }

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("#root element missing in index.html");

  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <MutationErrorReporter />
          <ErrorBoundary label="全域">
            {/* v7 future flags (ADR-0098).

                Both were printing a console warning on **every** page load, and
                `v7_relativeSplatPath` is not cosmetic here: this app routes
                unknown paths through a `path="*"` catch-all that renders
                `<Navigate to="/dashboard" />`. React Router 7 changes how a
                splat route resolves relative paths, so upgrading without opting
                in would change what that catch-all does — and the upgrade is
                forced by `^6.27.0` once v7 lands.

                Opting in now is the point of the flags: the v6 behaviour is
                what runs until someone upgrades the dependency. Verified by
                loading `/login` and a catch-all path afterwards. */}
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <App />
            </BrowserRouter>
          </ErrorBoundary>
        </ToastProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
